/**
 * eReg Intake (Phase 5 — plan §5.7)
 *
 * The platform's one bulk document inbox (replaces the Intelligence
 * "Upload Queue" and the legacy email ingest queue). Admins drop up to 20
 * files (zips expand server-side); AI suggests the study, type, and name;
 * each item is previewed, filed into a study / site / network folder, or
 * discarded.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Inbox,
  Upload as UploadIcon,
  Eye,
  FolderInput,
  Trash2,
  FileText,
  FlaskConical,
  Loader2,
  RotateCcw,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEreg } from '../contexts/EregContext';
import * as eregApi from '../lib/api';
import {
  PageHeader,
  RefreshButton,
  FilterBar,
  FilterChip,
  EmptyState,
  Modal,
  ConfirmModal,
  StudyPicker,
  fieldCls,
  chipCls,
  primaryBtnCls,
  useToast,
} from '../../ui';
import type { RegistryStudy } from '../../ui';
import { openPresigned } from '../../ui/studyRoles';
import type { IntakeItem, IntakeStatus, Folder, DocumentType } from '../types';

type StatusFilter = IntakeStatus;

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'filed', label: 'Filed' },
  { value: 'discarded', label: 'Discarded' },
];

// Mirrors the backend intake allow-list (ALLOWED_TYPES + ZIP/markdown) —
// image/tiff is rejected server-side, so don't offer it here.
const ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.md,.csv,.zip';

function formatBytes(bytes?: number | null): string {
  if (bytes === undefined || bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Flatten a folder tree (or an already-flat list) into path order. */
function flattenFolders(tree: Folder[]): Folder[] {
  const out: Folder[] = [];
  const seen = new Set<string>();
  const walk = (nodes: Folder[]) => {
    for (const node of nodes || []) {
      if (!seen.has(node.id)) {
        seen.add(node.id);
        out.push(node);
      }
      if (node.children && node.children.length > 0) walk(node.children);
    }
  };
  walk(tree || []);
  return out.sort((a, b) => (a.path || a.name).localeCompare(b.path || b.name));
}

export function Intake() {
  const { currentSite } = useAuth();
  const { documentTypes } = useEreg();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [items, setItems] = useState<IntakeItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [fileTarget, setFileTarget] = useState<IntakeItem | null>(null);
  const [discardTarget, setDiscardTarget] = useState<IntakeItem | null>(null);
  const [discarding, setDiscarding] = useState(false);

  const siteId = currentSite?.id;
  const networkId = currentSite?.network_id;

  // P-9: filter switches race — a slow older response must not overwrite a
  // newer one, so each load takes a token and stale results are dropped.
  const loadSeq = useRef(0);
  const loadItems = useCallback(
    async (status: StatusFilter = statusFilter) => {
      if (!siteId) return;
      const token = ++loadSeq.current;
      setLoading(true);
      try {
        const data = await eregApi.getIntakeItems(siteId, status);
        if (token !== loadSeq.current) return; // superseded by a newer load
        setItems(data);
        if (status === 'pending') setPendingCount(data.length);
      } catch (err) {
        if (token !== loadSeq.current) return;
        console.error('[eReg] Failed to load intake items:', err);
        toast('Failed to load intake items', { type: 'error' });
      } finally {
        if (token === loadSeq.current) setLoading(false);
      }
    },
    [siteId, statusFilter, toast]
  );

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleUpload = async (fileList: FileList | File[]) => {
    if (!siteId) return;
    const files = Array.from(fileList);
    if (files.length === 0) return;
    if (files.length > 20) {
      toast('Upload up to 20 files at a time', { type: 'error' });
      return;
    }
    setUploading(true);
    try {
      const result = await eregApi.uploadIntakeFiles(siteId, files);
      const added = result.items?.length || 0;
      if (added > 0) {
        toast(`${added} file${added === 1 ? '' : 's'} added to intake`);
      }
      (result.errors || []).forEach((e) =>
        toast(`${e.file}: ${e.error}`, { type: 'error', duration: 8000 })
      );
      setStatusFilter('pending');
      await loadItems('pending');
    } catch (err) {
      console.error('[eReg] Intake upload failed:', err);
      toast('Failed to upload files', { type: 'error' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePreview = async (item: IntakeItem) => {
    const opened = await openPresigned(
      async () => (await eregApi.getIntakePreviewUrl(item.id)).url
    );
    if (!opened) toast('Failed to open preview', { type: 'error' });
  };

  const handleDiscard = async () => {
    if (!discardTarget) return;
    setDiscarding(true);
    try {
      await eregApi.discardIntakeItem(discardTarget.id);
      toast('Item discarded — restore it from the Discarded tab within 30 days');
      setDiscardTarget(null);
      await loadItems();
    } catch (err) {
      console.error('[eReg] Failed to discard intake item:', err);
      toast('Failed to discard item', { type: 'error' });
    } finally {
      setDiscarding(false);
    }
  };

  // P-9: discard is undoable — the staged bytes are retained for 30 days.
  const handleRestore = async (item: IntakeItem) => {
    try {
      await eregApi.restoreIntakeItem(item.id);
      toast('Item restored to the pending queue');
      await loadItems();
    } catch (err: any) {
      console.error('[eReg] Failed to restore intake item:', err);
      toast(err?.response?.data?.error || 'Failed to restore item', { type: 'error' });
    }
  };

  if (!currentSite) {
    return (
      <div className="mx-auto max-w-7xl">
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center dark:border-gray-700 dark:bg-gray-800">
          <Inbox size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <h2 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">No Site Selected</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Select a site from the header to use Intake.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader
        title="Intake"
        icon={Inbox}
        count={pendingCount}
        subtitle="One inbox for bulk document filing — AI suggests the study, type, and name"
        actions={<RefreshButton onClick={() => loadItems()} loading={loading} />}
      />

      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleUpload(e.dataTransfer.files);
        }}
        className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors duration-150 ${
          dragOver
            ? 'border-primary-400 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
            : 'border-gray-300 hover:border-primary-400 dark:border-gray-600 dark:hover:border-primary-500'
        }`}
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-sm text-primary-600 dark:text-primary-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            Uploading…
          </div>
        ) : (
          <>
            <UploadIcon size={32} className="mx-auto mb-2 text-gray-400 dark:text-gray-500" />
            <p className="mb-1 text-sm text-gray-600 dark:text-gray-400">
              Drag and drop files here, or{' '}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="font-medium text-primary-600 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300"
              >
                browse
              </button>
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Up to 20 files — PDFs, Word, Excel, text, images, and .zip (zips are expanded automatically)
            </p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          onChange={(e) => e.target.files && handleUpload(e.target.files)}
          className="hidden"
        />
      </div>

      {/* Status filter — chromeless */}
      <FilterBar>
        {STATUS_FILTERS.map((f) => (
          <FilterChip
            key={f.value}
            active={statusFilter === f.value}
            onClick={() => setStatusFilter(f.value)}
          >
            {f.label}
            {f.value === 'pending' && pendingCount !== null && pendingCount > 0 && (
              <span className="tabular-nums">({pendingCount})</span>
            )}
          </FilterChip>
        ))}
      </FilterBar>

      {/* Items */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Inbox}
          variant="card"
          title={statusFilter === 'pending' ? 'Intake is clear' : `No ${statusFilter} items`}
          hint={
            statusFilter === 'pending'
              ? 'Drop files above to add documents for filing.'
              : 'Items appear here once they are ' + (statusFilter === 'filed' ? 'filed into a folder.' : 'discarded.')
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">File</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">AI Suggestion</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Uploaded</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {items.map((item) => (
                  <tr key={item.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2.5">
                        <FileText size={18} className="mt-0.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                        <div className="min-w-0">
                          <p className="max-w-[16rem] truncate text-sm font-medium text-gray-900 dark:text-gray-100" title={item.original_filename}>
                            {item.original_filename}
                          </p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {[formatBytes(item.file_size), item.page_count ? `${item.page_count} page${item.page_count === 1 ? '' : 's'}` : '']
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.suggested_protocol || item.suggested_document_type || item.suggested_name ? (
                        <div className="space-y-0.5" title={item.suggestion_reason || undefined}>
                          <div className="flex flex-wrap items-center gap-1.5">
                            {item.suggested_protocol && (
                              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-400">
                                <FlaskConical size={10} />
                                {item.suggested_protocol.study_nickname || item.suggested_protocol.protocol_number}
                              </span>
                            )}
                            {item.suggested_document_type && (
                              <span className="text-xs text-gray-600 dark:text-gray-300">{item.suggested_document_type}</span>
                            )}
                            {typeof item.suggestion_confidence === 'number' && (
                              <span className="text-xs tabular-nums text-gray-400 dark:text-gray-500">
                                {Math.round(item.suggestion_confidence * 100)}%
                              </span>
                            )}
                          </div>
                          {item.suggestion_reason && (
                            <p className="max-w-[18rem] truncate text-xs text-gray-400 dark:text-gray-500">
                              {item.suggestion_reason}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">No suggestion</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 dark:text-gray-100">
                        {item.uploader?.full_name || item.uploader?.email || '—'}
                      </p>
                      <p className="whitespace-nowrap text-xs tabular-nums text-gray-400 dark:text-gray-500">
                        {new Date(item.created_at).toLocaleString()}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {item.status === 'pending' ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => handlePreview(item)}
                            title="Preview"
                            aria-label={`Preview ${item.original_filename}`}
                            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setFileTarget(item)}
                            className={primaryBtnCls}
                          >
                            <FolderInput size={14} />
                            File
                          </button>
                          <button
                            type="button"
                            onClick={() => setDiscardTarget(item)}
                            title="Discard"
                            aria-label={`Discard ${item.original_filename}`}
                            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-red-600 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-red-400 dark:hover:bg-red-900/20 sm:h-8 sm:w-8"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-end gap-0.5">
                          <div className="flex items-center gap-1.5">
                            {item.status === 'filed' ? (
                              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
                                Filed{item.filed_document ? ` as ${item.filed_document.document_name}` : ''}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700/60 dark:text-gray-300">
                                {item.purged_at ? 'Discarded (purged)' : 'Discarded'}
                              </span>
                            )}
                            {item.status === 'discarded' && !item.purged_at && (
                              <button
                                type="button"
                                onClick={() => handleRestore(item)}
                                title="Restore to the pending queue"
                                aria-label={`Restore ${item.original_filename}`}
                                className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
                              >
                                <RotateCcw size={16} />
                              </button>
                            )}
                            {!item.purged_at && (
                              <button
                                type="button"
                                onClick={() => handlePreview(item)}
                                title="Preview"
                                aria-label={`Preview ${item.original_filename}`}
                                className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:h-8 sm:w-8"
                              >
                                <Eye size={16} />
                              </button>
                            )}
                          </div>
                          {item.filed_at && (
                            <span className="text-xs tabular-nums text-gray-400 dark:text-gray-500">
                              {new Date(item.filed_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* File modal */}
      {fileTarget && siteId && (
        <FileIntakeModal
          item={fileTarget}
          siteId={siteId}
          networkId={networkId}
          documentTypes={documentTypes}
          onClose={() => setFileTarget(null)}
          onFiled={(result) => {
            setFileTarget(null);
            if (result.key_document?.occupied_by_document_id) {
              toast('Filed — a pin decision is available in the eReg study view', { type: 'info', duration: 6000 });
            } else {
              toast('Document filed');
            }
            loadItems();
          }}
        />
      )}

      {/* Discard confirm */}
      <ConfirmModal
        isOpen={!!discardTarget}
        onClose={() => setDiscardTarget(null)}
        onConfirm={handleDiscard}
        title="Discard item?"
        message={
          discardTarget ? (
            <>
              <span className="font-medium text-gray-900 dark:text-gray-100">{discardTarget.original_filename}</span>{' '}
              will be removed from the intake queue without being filed.
            </>
          ) : undefined
        }
        confirmLabel="Discard"
        danger
        loading={discarding}
      />
    </div>
  );
}

// =====================================================
// File modal — pick a target zone + folder, confirm metadata, file it
// =====================================================

type TargetZone = 'study' | 'site' | 'network';

function FileIntakeModal({
  item,
  siteId,
  networkId,
  documentTypes,
  onClose,
  onFiled,
}: {
  item: IntakeItem;
  siteId: string;
  networkId?: string;
  documentTypes: DocumentType[];
  onClose: () => void;
  onFiled: (result: eregApi.IntakeFileResponse) => void;
}) {
  const [zone, setZone] = useState<TargetZone>('study');
  const [selectedStudy, setSelectedStudy] = useState<RegistryStudy | null>(
    item.suggested_protocol
      ? {
          id: item.suggested_protocol.id,
          site_id: item.site_id,
          protocol_number: item.suggested_protocol.protocol_number,
          study_nickname: item.suggested_protocol.study_nickname,
        }
      : null
  );
  const [folders, setFolders] = useState<Folder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [folderId, setFolderId] = useState('');
  const folderTouched = useRef(false);

  const [documentName, setDocumentName] = useState(
    item.suggested_name || item.original_filename.replace(/\.[^.]+$/, '')
  );
  const [documentType, setDocumentType] = useState(item.suggested_document_type || '');
  const [versionNumber, setVersionNumber] = useState('1.0');
  // U-2: without these, intake-filed certifications silently escaped
  // Expiring-Soon tracking (the direct Upload form always had them).
  const [documentDate, setDocumentDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [description, setDescription] = useState('');
  const [filing, setFiling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const studyId = zone === 'study' ? selectedStudy?.id : undefined;

  // Load folders for the chosen target
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setError(null);
      if (zone === 'study' && !studyId) {
        setFolders([]);
        return;
      }
      setFoldersLoading(true);
      try {
        let tree: Folder[] = [];
        if (zone === 'study' && studyId) {
          tree = await eregApi.getProtocolFolders(studyId);
        } else if (zone === 'site') {
          tree = await eregApi.getSiteFolders(siteId);
        } else if (zone === 'network' && networkId) {
          tree = await eregApi.getNetworkFolders(networkId);
        }
        if (!cancelled) {
          setFolders(flattenFolders(tree));
          folderTouched.current = false;
        }
      } catch (err) {
        console.error('[eReg] Failed to load folders for intake filing:', err);
        if (!cancelled) {
          setFolders([]);
          setError('Failed to load folders for the selected target');
        }
      } finally {
        if (!cancelled) setFoldersLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [zone, studyId, siteId, networkId]);

  // Preselect ONLY a folder whose default type matches the chosen type —
  // never folders[0] (P-9: the alphabetical fallback made one-click filing
  // land in the first folder regardless of fit). No match = explicit choice.
  useEffect(() => {
    if (folders.length === 0) {
      setFolderId('');
      return;
    }
    if (folderTouched.current && folders.some((f) => f.id === folderId)) return;
    const match = folders.find(
      (f) => f.default_document_type && f.default_document_type === documentType
    );
    setFolderId(match ? match.id : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders, documentType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!folderId || !documentName.trim() || !documentType) {
      setError('Choose a folder, name, and document type');
      return;
    }
    setFiling(true);
    setError(null);
    try {
      const result = await eregApi.fileIntakeItem(item.id, {
        folder_id: folderId,
        document_name: documentName.trim(),
        document_type: documentType,
        version_number: versionNumber.trim() || undefined,
        document_date: documentDate || undefined,
        expiration_date: expirationDate || undefined,
        description: description.trim() || undefined,
      });
      onFiled(result);
    } catch (err: any) {
      console.error('[eReg] Failed to file intake item:', err);
      setError(err?.response?.data?.error || 'Failed to file document');
    } finally {
      setFiling(false);
    }
  };

  const typeInList = documentTypes.some((t) => t.name === documentType);

  return (
    <Modal isOpen onClose={onClose} title="File Document" maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="rounded-lg bg-gray-50 p-3 text-sm dark:bg-gray-900/40">
          <p className="font-medium text-gray-900 dark:text-gray-100">{item.original_filename}</p>
          <p className="text-gray-500 dark:text-gray-400">
            {[formatBytes(item.file_size), item.mime_type].filter(Boolean).join(' · ')}
          </p>
        </div>

        {/* Target zone */}
        <div>
          <p className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">File to</p>
          <div className="flex flex-wrap items-center gap-1.5">
            <button type="button" onClick={() => setZone('study')} aria-pressed={zone === 'study'} className={chipCls(zone === 'study')}>
              Study
            </button>
            <button type="button" onClick={() => setZone('site')} aria-pressed={zone === 'site'} className={chipCls(zone === 'site')}>
              Site documents
            </button>
            <button
              type="button"
              onClick={() => setZone('network')}
              aria-pressed={zone === 'network'}
              disabled={!networkId}
              className={`${chipCls(zone === 'network')} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              Network documents
            </button>
          </div>
        </div>

        {/* Study picker */}
        {zone === 'study' && (
          <div>
            <p className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Study</p>
            {selectedStudy ? (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 dark:border-primary-800 dark:bg-primary-900/20">
                <div className="flex min-w-0 items-center gap-2">
                  <FlaskConical size={16} className="flex-shrink-0 text-primary-600 dark:text-primary-400" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {selectedStudy.protocol_number}
                    </p>
                    {selectedStudy.study_nickname && (
                      <p className="truncate text-xs text-gray-500 dark:text-gray-400">{selectedStudy.study_nickname}</p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedStudy(null)}
                  aria-label="Change study"
                  className="rounded p-1 text-gray-400 transition-colors duration-150 hover:bg-primary-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-primary-900/40 dark:hover:text-gray-200"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <StudyPicker
                siteId={siteId}
                onSelect={(study) => setSelectedStudy(study)}
                selectedId={null}
                allowCreate={false}
              />
            )}
          </div>
        )}

        {/* Folder */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Folder *</label>
          {foldersLoading ? (
            <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
              Loading folders…
            </p>
          ) : (
            <select
              value={folderId}
              onChange={(e) => {
                folderTouched.current = true;
                setFolderId(e.target.value);
              }}
              className={`w-full ${fieldCls}`}
              required
              disabled={folders.length === 0}
            >
              <option value="">
                {zone === 'study' && !selectedStudy
                  ? 'Select a study first…'
                  : folders.length === 0
                    ? 'No folders available'
                    : 'Select folder…'}
              </option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.path || folder.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Name */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Document Name *</label>
          <input
            type="text"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
            className={`w-full ${fieldCls}`}
            required
          />
        </div>

        {/* Type + Version */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Document Type *</label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className={`w-full ${fieldCls}`}
              required
            >
              <option value="">Select type…</option>
              {!typeInList && documentType && <option value={documentType}>{documentType}</option>}
              {documentTypes.map((type) => (
                <option key={type.id} value={type.name}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Version</label>
            <input
              type="text"
              value={versionNumber}
              onChange={(e) => setVersionNumber(e.target.value)}
              placeholder="1.0"
              className={`w-full ${fieldCls}`}
            />
          </div>
        </div>

        {/* Dates — expiration feeds the Expiring-Soon tracker */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Document Date</label>
            <input
              type="date"
              value={documentDate}
              onChange={(e) => setDocumentDate(e.target.value)}
              className={`w-full ${fieldCls}`}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Expiration Date</label>
            <input
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              className={`w-full ${fieldCls}`}
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Optional notes about this document"
            className={`w-full ${fieldCls}`}
          />
        </div>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={filing || !folderId || !documentName.trim() || !documentType}
            className={primaryBtnCls}
          >
            {filing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderInput size={14} />}
            {filing ? 'Filing…' : 'File Document'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default Intake;
